import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import { Editor } from "@tinymce/tinymce-react";
//import "../../../node_modules/tinymce/icons/default";
import { getHTMLContentCSS } from "../../CommonWeb/Common";
import "./HTMLEditorComponent.css";
import HTMLLinksModalClass from "./HTMLLinksModalClass";
import HTMLInsertDialog from "./HTMLInsertDialog";
import { useSelector, useDispatch } from "react-redux";
import { slice1Actions } from "../../store/reducer";

// A list marker's size comes from the <li> element's OWN font-size, never
// from an inline descendant like a font-size span — so a bigger-sized list
// item still showed a normal-size number/bullet. Same root cause and same
// fix as mobile's identical bug (HTMLEditor.js's _wrapListWithFontSizeJS —
// see its own comment for the full history of what was tried first and
// didn't work). TinyMCE's native fontsizeselect/bullist/numlist all go
// through the same underlying document.execCommand mechanics as mobile's
// editor, so this is presumably the identical bug — found 2026-10-03 after
// a user reported it in the web editor too, debugged here instead of
// mobile since there's no native-rebuild cycle and Chrome's engine behaves
// more predictably than a mobile WebView.
//
// Earlier attempts wrapped the font-size in a NEW ancestor element around
// the whole <ol>/<ul> (first <span>, then <div> after discovering TinyMCE's
// own content schema silently strips a <span> wrapping block-level content
// like an <ol> — a <span> is phrasing content and isn't supposed to contain
// one). The <div> version worked at first but then ALSO disappeared after
// a subsequent list toggle ("it loses the div") — toggling a list off/on
// evidently doesn't reliably preserve an arbitrary new ancestor TinyMCE
// didn't itself create. The robust fix: don't introduce any new element at
// all — set font-size directly on the EXISTING <ol>/<ul> itself. Since
// font-size is inherited and passes the *computed* value down regardless
// of which ancestor carries it, the <li> (and its marker) inherit exactly
// the same way they would from a wrapper, but there's no new node for
// TinyMCE's schema/toggle logic to object to or discard — it's just an
// inline style on an element TinyMCE already expects to be there. Found
// 2026-10-03.
//
// Only handles the case where EVERY item in the list shares the exact same
// single sized span (the common "this whole list is bigger" case) — a list
// with only some items sized, or mixed sizes, is left alone rather than
// guessing. When a list that WAS previously matched no longer matches
// (size removed, or content edited so it's no longer uniform), this also
// explicitly clears any font-size/padding-left this function itself set
// earlier — otherwise the <ol> would keep forcing the old size on content
// that no longer has any, the first time this matters being exactly the
// question "when it doesn't need it?".
function wrapListWithFontSize(editor) {
  const body = editor.getBody();
  const lists = body.querySelectorAll("ol, ul");
  lists.forEach((list) => {
    const items = list.querySelectorAll(":scope > li");
    if (items.length === 0) return;
    let sharedSize = null;
    const candidates = [];
    // anyCandidate (not just allMatch) — a successfully hoisted list has
    // PLAIN TEXT items with no inner span at all, which looks IDENTICAL to
    // "never had a match" (both have zero candidates). Without this
    // distinction, re-running this function on an already-hoisted list
    // (which happens, since it's called from more than one trigger) would
    // see "no candidate found" and WIPE the size it had just correctly
    // set — confirmed 2026-10-03 as the actual cause of "font-size is in
    // the span and not the ol" on a second look, not a timing issue.
    // anyCandidate tracks whether a span was found ANYWHERE, so clearing
    // only happens on real evidence of a change (some items sized
    // differently or inconsistently), never on the simple absence of any
    // span at all — which is the expected, correct steady state right
    // after a successful hoist.
    let anyCandidate = false;
    let allMatch = true;
    for (const li of items) {
      const liText = li.textContent || "";
      let candidate = null;
      if (liText) {
        const walker = document.createTreeWalker(li, NodeFilter.SHOW_ELEMENT);
        let node;
        while ((node = walker.nextNode())) {
          if (node.style && node.style.fontSize && (node.textContent || "") === liText) {
            candidate = node;
            break;
          }
        }
      }
      if (candidate) {
        anyCandidate = true;
        if (sharedSize === null) sharedSize = candidate.style.fontSize;
        else if (sharedSize !== candidate.style.fontSize) allMatch = false;
        candidates.push(candidate);
      } else {
        allMatch = false;
      }
    }
    const fullyMatched = allMatch && sharedSize && candidates.length === items.length;
    if (fullyMatched) {
      candidates.forEach((c, idx) => {
        const li = items[idx];
        const par = c.parentNode;
        while (c.firstChild) par.insertBefore(c.firstChild, c);
        par.removeChild(c);
        // Strip stray <br> siblings left in this <li> — TinyMCE inserts a
        // "bogus" <br> filler to keep an otherwise-empty block from
        // collapsing, and toggling the list off/on repeatedly (e.g. while
        // testing this very fix) left a NEW one behind each time without
        // ever clearing the previous one, visibly accumulating. Safe to
        // remove unconditionally here specifically because the match above
        // already confirmed this li's ENTIRE text content came from the
        // span we just unwrapped — any <br> siblings are provably pure
        // filler, not a real line break the user typed. Found 2026-10-03.
        Array.from(li.querySelectorAll(":scope > br")).forEach((br) => br.remove());
      });
      list.style.fontSize = sharedSize;
      // em, not a fixed px bump — the browser's default <ol> padding-left
      // (~40px) doesn't scale with font-size, so it's only ever barely
      // enough for normal-size markers; the gap grows with size. 2em
      // scales WITH the now-bigger inherited font-size at every size.
      list.style.paddingLeft = "2em";
    } else if (anyCandidate && list.style.fontSize) {
      // Real evidence this list's sizing changed (some items sized
      // differently now, or inconsistently) — clear the stale value
      // rather than leaving the <ol> stuck forcing an old size. Does NOT
      // fire on simple absence of any candidate, which is the normal
      // already-hoisted steady state (see anyCandidate's own comment).
      list.style.fontSize = "";
      list.style.paddingLeft = "";
    }
  });
}

// Toggling a list OFF (execCommand('InsertOrderedList'/'InsertUnorderedList')
// on a selection already inside one) discards the <ol>/<ul> entirely,
// converting each <li> into a plain paragraph — and since
// wrapListWithFontSize stores the size ON that <ol>, toggling it off threw
// the size away with nowhere for it to land (confirmed 2026-10-03: "after
// toggling off the li, it drops the ol and the size"). Fix: capture the
// list's font-size and each item's text BEFORE the toggle runs (via
// BeforeExecCommand, in case this turns out to be an off-toggle), then
// AFTER it runs, if that same text is now sitting in a plain block with no
// font-size of its own (i.e. it really was un-listified), re-apply the
// captured size there directly.
function captureListFontSizeBeforeToggle(editor) {
  const node = editor.selection.getNode();
  const list = editor.dom.getParent(node, "ol,ul");
  if (!list || !list.style.fontSize) return null;
  const items = list.querySelectorAll(":scope > li");
  return {
    size: list.style.fontSize,
    texts: Array.from(items).map((li) => li.textContent || ""),
  };
}

function restoreFontSizeAfterUnlistify(editor, captured) {
  if (!captured) return;
  const body = editor.getBody();
  for (const text of captured.texts) {
    if (!text) continue;
    // First choice: an existing block (p/div/heading) holding exactly this
    // text, with no font-size of its own yet and no longer inside a list.
    const blocks = body.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6");
    let matchedBlock = null;
    for (const block of blocks) {
      if (
        (block.textContent || "") === text &&
        !block.style.fontSize &&
        !editor.dom.getParent(block, "ol,ul")
      ) {
        matchedBlock = block;
        break;
      }
    }
    if (matchedBlock) {
      matchedBlock.style.fontSize = captured.size;
      continue;
    }
    // No wrapping block at all — this editor's forced_root_block:"" (see
    // this component's own init comment) means un-listified content can
    // land as a bare text node directly in the body, with nothing to hang
    // a style on. Wrap just that text node in a new span instead — safe
    // here, unlike wrapping a span around a block-level <ol>/<ul> (which
    // TinyMCE's schema strips): a span around plain text is completely
    // normal. Found 2026-10-03 — the block-only version above silently did
    // nothing in exactly this bare-text-node case.
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if ((node.textContent || "").trim() === text.trim() && node.parentNode && !editor.dom.getParent(node.parentNode, "ol,ul")) {
        const span = document.createElement("span");
        span.setAttribute("style", `font-size:${captured.size}`);
        node.parentNode.insertBefore(span, node);
        span.appendChild(node);
        break;
      }
    }
  }
}

const HTMLEditorComponent = (props) => {
  //console.log("HTMLReader", props);
  const [ShowHTML, setShowHTML] = useState(false);
  const [ShowCSS, setShowCSS] = useState(true);

  const [HTMLText, setHTMLText] = useState("");
  const [ContentStyle, setContentStyle] = useState("");

  const [HTMLtoShow, setHTMLtoShow] = useState("");
  //const [CSStoApply, setCSStoApply] = useState("");
  const [FullScreen, setFullScreen] = useState("md");
  const [ShowHTMLLinks, setShowHTMLLinks] = useState(false);
  const [ShowHTMLInsertDialog, setShowHTMLInsertDialog] = useState(false);
  const [SavedHTML, setSavedHTML] = useState(false);

  let user_info = useSelector((state) => state.user_info);
  let guide_meta = useSelector((state) => state.guide_meta);
  let GuideType = useSelector((state) => state.GuideType);
  let imageFolder = useSelector((state) => state.imageFolder);
  const editorRef = useRef(null);
    const dispatch = useDispatch();

  useEffect(() => {
    console.log("useEffect");
    const html = props.html;
    setHTMLtoShow(html);
    setSavedHTML(html);
    setContentStyle(getCSS(html));
    setShowCSS(false);
  }, [props.html]);

  const handleClose = () => {
    if (ShowCSS) {
      setShowCSS(false);
      return;
    }
    props.onHide();
  };

  const handleEditorChange = (e) => {
    //console.log("Content was updated:", e.target.getContent());
  };

  const insertLink = () => {
    console.log("insertLink");
    setShowHTMLLinks(true);
    //const link = `<a href="urllink:https://www.google.com">google.com<\a>`;
    //let link = `<a href="urllink:https://www.google.com"><button style="border: none; padding:32px; margin:10px; color:white; border-radius:10%; font-size:36px; background-color: #008CBA;">google.com</button></a>&nbsp;`;

    //editorRef.current.insertContent(link);
  };

  const insertLinkCallback = (link) => {
    let content = "";
    if (link.linkUI === "Text only") {
      content = `<a href="${link.url}">${link.text}<\a>`;
    } else if (link.linkUI === "With Image" && link.text !== "") {
      content = `<a href="${link.url}"><button style="border: none; background: none; border-radius: 10%; font-size: 110%; padding:5px;background-size:cover;"><img style="border-radius: 10%;" src="${link.imgURL}" alt="image" width="350"/><br>${link.text}</button></a>&nbsp;`;
    } else if (link.linkUI === "With Image") {
      content = `<a href="${link.url}"><img src="${link.imgURL}" alt="image" style="max-width: 100%; width:${link.imageWidth}px; border-radius:10%; margin:10px;"></a>&nbsp;&nbsp;`;
    } else if (link.linkUI === "With Icon") {
      content = `<a href="${link.url}"><img src="${link.imgURL}" alt="icon" style="max-width: 100%; margin:10px;"></a>&nbsp;&nbsp;`;
    } else {
      content = `<a href="${link.url}"><button style="border: none; padding:32px; margin:10px; color:white; border-radius:10%; font-size:200%; background-color: #008CBA;">${link.text}</button></a>&nbsp;`;
    }
    console.log("insertLinkCallback", content, link.text, link.url);
    editorRef.current.insertContent(content);
  };

  const insertContent = () => {
    setShowHTMLInsertDialog(true);
  };

  const insertContentCallback = (content) => {
    if (editorRef.current) {
      console.log(content.payload.html);
      editorRef.current.insertContent(content.payload.html);
      // Separate, follow-up call — NOT concatenated onto the same
      // insertContent() string above. Concatenating made the <br> the very
      // last node with nothing after it yet, and TinyMCE's content-cleanup
      // pass strips a trailing <br> like that as insignificant immediately
      // on insert (confirmed: it silently failed). A distinct call right
      // after, the same execCommand the manual "Line Break" button below
      // uses, reliably commits instead. Fixed 2026-10-03 after confirming
      // the concatenated version didn't actually work.
      editorRef.current.execCommand("mceInsertContent", false, "<br>");
    }
  };

  const getContent = () => {
    if (editorRef.current) {
      return editorRef.current.getContent();
    }
  };

  const setContent = (content) => {
    if (editorRef.current) {
      //console.log("setContent", content);
      return editorRef.current.setContent(content);
    }
  };

  const getCSS = (html) => {
    let index1 = html.indexOf("<style>", 0);
    let index2 = html.indexOf("</style>", index1 + 1);
    if (index1 !== -1 && index2 !== -1) {
      return html.substring(index1 + 7, index2);
    }
    return "";
  };

  // useEffect(() => {
  //let html = props.html;
  //let content_style = getCSS(html);
  //console.log("content_style", content_style);
  //if (html.indexOf(`<div class="home-resize-panel">`, 0) === -1) html = < 450 ? "lg" : "md"(html);
  //setHTMLtoShow(html);
  //setCSStoApply(content_style);
  //console.log("content_style", content_style);
  // }, []);

  const handleOK = () => {
    if (ShowCSS) {
      setShowCSS(false);
      return;
    }
    props.onHide();
    const content = getContent();
    //console.log("content", content);
    let html = content;
    if (content !== "") html = "<style>" + ContentStyle + "</style>" + content;
    // html = html.replace(/&nbsp;/g, " ");
    if (props.callback != null) {
      //html = html.replace("</style><br>", "</style>");
      //html = html.replace(/body {  font-size: 1.4em;}/, "body {  font-size: 3em;}");
      //html = html.replace(/strong>/g, "b>");
      props.callback(html);
    }
    console.log(html);
  };

  const handleHTMLChanged = (text) => {
    let html = "<style>" + ContentStyle + "</style>" + text;
    if (props.callback_rt != null) {
      //("</style><br>", "</style>");
      props.callback_rt(html);
    }
    setSavedHTML(text);
    //console.log(text);
    //setHTMLText(text);
  };

  const handleCSSChanged = (event) => {
    console.log("handleCSSChanged");
    const content_style = event.target.value;
    setContentStyle(content_style);
    if (props.callback_rt != null) {
      props.callback_rt("<style>" + content_style + "</style>" + SavedHTML);
      //console.log("handleCSSChanged", content);
    }
    //console.log(content_style);
  };

  const editCSS = () => {
    console.log("editCSS");
    if (!ShowCSS) setHTMLtoShow(getContent());
    setShowCSS(!ShowCSS);
  };

  // function handleFullScreen(breakpoint) {
  //   setShowModal(false);
  //   setFullScreen(breakpoint);
  //   setShowModal(true);
  //   //setShow(true);
  // }

  function formatHTML(html) {
    var indent = "\n";
    var tab = "\t";
    var i = 0;
    var pre = [];

    html = html
      .replace(new RegExp("<pre>((.|\\t|\\n|\\r)+)?</pre>"), function (x) {
        pre.push({ indent: "", tag: x });
        return "<--TEMPPRE" + i++ + "/-->";
      })
      .replace(new RegExp("<[^<>]+>[^<]?", "g"), function (x) {
        var ret;
        var tag = /<\/?([^\s/>]+)/.exec(x)[1];
        var p = new RegExp("<--TEMPPRE(\\d+)/-->").exec(x);

        if (p) pre[p[1]].indent = indent;

        if (
          [
            "area",
            "base",
            "br",
            "col",
            "command",
            "embed",
            "hr",
            "img",
            "input",
            "keygen",
            "link",
            "menuitem",
            "meta",
            "param",
            "source",
            "track",
            "wbr",
          ].indexOf(tag) >= 0
        )
          // self closing tag
          ret = indent + x;
        else {
          if (x.indexOf("</") < 0) {
            //open tag
            if (x.charAt(x.length - 1) !== ">")
              ret = indent + x.substr(0, x.length - 1) + indent + tab + x.substr(x.length - 1, x.length);
            else ret = indent + x;
            !p && (indent += tab);
          } else {
            //close tag
            indent = indent.substr(0, indent.length - 1);
            if (x.charAt(x.length - 1) !== ">")
              ret = indent + x.substr(0, x.length - 1) + indent + x.substr(x.length - 1, x.length);
            else ret = indent + x;
          }
        }
        return ret;
      });

    for (i = pre.length; i--; ) {
      html = html.replace(
        "<--TEMPPRE" + i + "/-->",
        pre[i].tag.replace("<pre>", "<pre>\n").replace("</pre>", pre[i].indent + "</pre>")
      );
    }

    let temp = html.charAt(0) === "\n" ? html.substr(1, html.length - 1) : html;
    return temp.replace(/(^[ \t]*\n)/gm, "");
  }

  function unformatHTML(html) {
    var i = 0;
    var pre = [];

    html = html
      .replace(new RegExp("<pre>((.|\\t|\\n|\\r)+)?</pre>"), function (x) {
        pre.push(x);
        return "<--TEMPPRE" + i++ + "/-->";
      })
      .replace(/\n/g, "")
      .replace(/\t/g, "");

    for (i = pre.length; i--; ) {
      html = html.replace("<--TEMPPRE" + i + "/-->", pre[i]);
    }

    html = html.replace(new RegExp("<pre>\\n"), "<pre>").replace(new RegExp("\\n\\t*</pre>"), "</pre>");
    return html;
  }
    
    const setImageFolder = (folder) => {
      dispatch(slice1Actions.setImageFolder({ value: folder }));
    };

  // if (ShowHTMLLinks)
  //   return (
  //     <HTMLLinksModalClass show={ShowHTMLLinks} onHide={() => setShowHTMLLinks(false)} callback={insertLinkCallback} />
  //   );
  // else
  //console.log("ContentStyle", ContentStyle);
  return (
    <div className="he-shell">
      <div className="he-header">
        <div className="he-header-icon">
          <FontAwesomeIcon icon={solid("pen-nib")} />
        </div>
        <div className="he-header-text">
          <span className="he-title">{props.title || "Edit Content"}</span>
        </div>
        <div className="he-mode-toggle">
          <button
            className={`he-mode-btn${!ShowCSS ? " active" : ""}`}
            onClick={() => { if (ShowCSS) editCSS(); }}
          >
            Content
          </button>
          <button
            className={`he-mode-btn${ShowCSS ? " active" : ""}`}
            onClick={() => { if (!ShowCSS) editCSS(); }}
          >
            Style
          </button>
        </div>
        {(props.embedded !== true || ShowCSS) && (
          <button className="he-save-btn" onClick={handleOK} title="Save">
            <FontAwesomeIcon icon={solid("check")} />
          </button>
        )}
        {(props.embedded !== true || ShowCSS) && (
          <button className="he-close" onClick={handleClose} title="Close">
            <FontAwesomeIcon icon={solid("xmark")} />
          </button>
        )}
      </div>
      <div className="he-body">
      {ShowCSS ? (
        <textarea
          className="he-css-editor"
          onChange={handleCSSChanged}
          value={ContentStyle}
        />
      ) : (
        <Editor
          //ref={editor}
          onInit={(evt, editor) => (editorRef.current = editor)}
          apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
          initialValue={HTMLtoShow}
          onEditorChange={(newText) => handleHTMLChanged(newText)}
          init={{
            force_br_newlines: true,
            force_p_newlines: false,
            forced_root_block: "", // Needed for 3.x

            fontsize_formats: "60% 80% 100% 120% 140% 160% 180% 200% 220% 240% 260% 280% 300% 350% 400%",
            // No toolbar button for inserting images — HTMLInsertDialog
            // ("Embed" button) is the one, non-confusing way to add images
            // (it picks from Firebase/Drive-hosted URLs, matching every
            // other image path in the app). The "image" plugin stays
            // loaded (below) purely so double-clicking an already-inserted
            // image still opens TinyMCE's own Edit Image dialog, which has
            // the width/height fields — that dialog-based resize doesn't
            // need the toolbar button, just the plugin. image_uploadtab
            // is kept false in case that dialog is ever reachable via the
            // image's context toolbar, so it can't offer a raw-file
            // upload (base64 data URI) as an alternate source.
            image_uploadtab: false,
            // Pictures are always LINKED, never stored inside the HTML: a pasted
            // picture would become a data: URI in the guide's one Firestore
            // document (1 MiB limit, see Common/guideSize.js). TinyMCE 5's
            // default, set explicitly so an upgrade can't change it.
            paste_data_images: false,
            // Advanced tab in that Edit Image dialog: spacing around an image
            // and its border, so text wrapped around a left/right-aligned
            // image (Photo Book custom pages) doesn't sit against it.
            image_advtab: true,
            content_css: "/HTMLEditor.css,HTMLEditor.css",
            content_style: getHTMLContentCSS() + "\n" + ContentStyle,
            height: 500,
            menubar: false,
            toolbar_mode: "wrap",
            //output: "/tinymce/icons/",
            plugins: [
              "advlist  lists  image", //autolink link
              "charmap print preview anchor ", //help
              "searchreplace visualblocks code",
              "insertdatetime media table paste wordcount fullscreen emoticons ",
            ],
            setup: function (editor) {
              // Deferred via double requestAnimationFrame, not run
              // synchronously inside the ExecCommand handler — mobile's
              // identical fix needed this because its WebView's
              // insertOrderedList/insertUnorderedList DOM restructuring
              // wasn't always fully committed by the very next line; kept
              // here too even though real browsers are better-behaved,
              // since it's zero-cost and avoids re-introducing that exact
              // class of bug if it turns out to apply here as well.
              //
              // wrapListToken: each trigger increments it and captures its
              // own value; the deferred call only actually runs if the
              // token is still current by the time it fires. Without this,
              // rapidly toggling the list on/off several times in a row
              // (exactly what testing this fix looks like) scheduled a
              // pile of overlapping deferred calls that could run against a
              // DOM state several toggles stale by the time they fired,
              // instead of just the latest one. Found 2026-10-03.
              //
              // Selection-preservation via TinyMCE's bookmark API was
              // tried and removed — not just ineffective but actively
              // HARMFUL: getBookmark(2, true) doesn't just read the
              // selection, it inserts real marker elements into the DOM at
              // the cursor position. Captured before the list toggle (to
              // survive both that and this fix's own mutations), the
              // marker sometimes ended up as a SIBLING to the font-size
              // span rather than inside it after the toggle restructured
              // things — which broke this fix's own "the <li>'s entire
              // text comes from exactly this one span" equality check
              // (confirmed 2026-10-03: a plain, correctly-structured
              // `<ol><li><span style="font-size:...">Cover</span></li></ol>`
              // still wasn't being hoisted, traced to this). User accepted
              // losing selection rather than chasing this further.
              let wrapListToken = 0;
              const scheduleWrapListWithFontSize = (captured) => {
                const myToken = ++wrapListToken;
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    if (myToken !== wrapListToken) return;
                    restoreFontSizeAfterUnlistify(editor, captured);
                    wrapListWithFontSize(editor);
                  });
                });
              };
              // Captured on the way IN (before the toggle runs) specifically
              // for the toggle-OFF case — see restoreFontSizeAfterUnlistify's
              // own comment. Harmless/unused when this turns out to be a
              // toggle-ON instead (wrapListWithFontSize handles that case on
              // its own).
              let pendingListFontSize = null;
              editor.on("BeforeExecCommand", (e) => {
                if (["InsertOrderedList", "InsertUnorderedList"].includes(e.command)) {
                  pendingListFontSize = captureListFontSizeBeforeToggle(editor);
                }
              });
              editor.on("ExecCommand", (e) => {
                if (["InsertOrderedList", "InsertUnorderedList"].includes(e.command)) {
                  const captured = pendingListFontSize;
                  pendingListFontSize = null;
                  scheduleWrapListWithFontSize(captured);
                }
              });
              // Separate hook for font-size specifically — modern TinyMCE's
              // fontsizeselect toolbar item applies via the Formatter API
              // (formatter.apply), not necessarily a raw execCommand, so an
              // ExecCommand-only listener could miss it entirely depending
              // on TinyMCE's internal version. FormatApply fires for either
              // path. Found 2026-10-03.
              editor.on("FormatApply", (e) => {
                if (e.format === "fontsize") {
                  scheduleWrapListWithFontSize();
                }
              });
              editor.ui.registry.addButton("linksBTN", {
                text: "Link",
                onAction: insertLink,
              });
              editor.ui.registry.addButton("embedBTN", {
                text: "Embed",
                onAction: insertContent,
              });
              // Explicit line-break button — Enter is configured to insert
              // <br> (force_br_newlines/forced_root_block above), but Enter
              // right after an inline element like an inserted image can
              // silently fail to commit one (see insertContentCallback's
              // own note). This gives an unambiguous, one-click way to add
              // a break regardless of cursor position. Found 2026-10-03.
              // Text-only, no `icon` — "line-break" isn't a real TinyMCE
              // icon name; an unrecognized icon id falls back to TinyMCE's
              // own deliberately jarring "unknown icon" placeholder (looks
              // like a stick of dynamite), confirmed 2026-10-03. Matches
              // linksBTN/embedBTN just above, which are also text-only.
              editor.ui.registry.addButton("lineBreakBTN", {
                text: "Line Break",
                onAction: () => editor.execCommand("mceInsertContent", false, "<br>"),
              });
              // editor.ui.registry.addButton("cssBTN", {
              //   text: "CSS",
              //   onAction: editCSS,
              // });
            },
            toolbar:
              "undo redo |  bold italic underline strikethrough | \
                  alignleft aligncenter alignright alignjustify | \
          bullist numlist checklist | forecolor backcolor casechange | outdent indent | fontselect fontsizeselect formatselect | charmap emoticons | linksBTN | embedBTN | lineBreakBTN | fullscreen |code ",
          }}
          onChange={handleEditorChange}
        />
      )}
      </div>
      <HTMLLinksModalClass
        show={ShowHTMLLinks}
        onHide={() => setShowHTMLLinks(false)}
        callback={insertLinkCallback}
        user_info={user_info}
        guide_meta={guide_meta}
        guide_type={GuideType}
        folder={imageFolder}
        setImageFolder={setImageFolder}
      />
      <HTMLInsertDialog
        show={ShowHTMLInsertDialog}
        onHide={() => setShowHTMLInsertDialog(false)}
        callback={insertContentCallback}
        user_info={user_info}
        section={null}
        folder={imageFolder}
        setImageFolder={setImageFolder}
      />
    </div>
  );
};

export default HTMLEditorComponent;
